'use client';

import { useTranslations } from 'next-intl';
import { Loader2 } from 'lucide-react';
import { SettingsCard, PickerRow } from '@/shared/ui';
import type { Palette } from '@/types/palette';

interface PaletteListProps {
  palettes: Palette[];
  isLoading: boolean;
  selectedPaletteId: number | null;
  onPaletteClick: (paletteId: number) => void;
  variant?: 'default' | 'compact';
}

export function PaletteList({
  palettes,
  isLoading,
  selectedPaletteId,
  onPaletteClick,
}: PaletteListProps) {
  const t = useTranslations('render.settings.colors');

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (palettes.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        {t('noPalettesAvailable')}
      </div>
    );
  }

  return (
    <SettingsCard>
      {palettes.map((palette) => {
        const paletteId = parseInt(palette.id);
        const isSelected = selectedPaletteId === paletteId;
        const colors = palette.attributes.colors ?? [];

        // Apple parity: leading content is a 5-stripe color swatch strip with
        // 4px corner radius (cf. ColorPickerView leadingContent in SwiftUI).
        const leading = (
          <div
            className="flex overflow-hidden rounded h-8"
            style={{ width: 80 }}
          >
            {colors.slice(0, 5).map((color, idx) => (
              <div
                key={idx}
                className="flex-1"
                style={{ backgroundColor: color }}
              />
            ))}
          </div>
        );

        return (
          <PickerRow
            key={palette.id}
            title={palette.attributes.name}
            leading={leading}
            isSelected={isSelected}
            onClick={() => onPaletteClick(paletteId)}
          />
        );
      })}
    </SettingsCard>
  );
}
